// PURPOSE: Remember personal saved views. CONTRACT: eight local URLs per membership. DEPENDS: React external store, session and toast. CEILING: preferences never grant record access.
import { useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { useSession } from './auth';
import { useToast } from './toast';
export type Pin = { id: string; name: string; url: string; created: string };
type Saved = { pins: Pin[]; collapsed: boolean };
const key='velox.pins.v1';
const stores=new Map<string,Saved>();
const listeners=new Set<()=>void>();
const emit=()=>listeners.forEach(fn=>fn());
const subscribe=(fn:()=>void)=>{listeners.add(fn);return()=>{listeners.delete(fn);};};
export const validPinUrl=(url:string)=>/^\/(?!\/)/.test(url) && !/[\\\u0000-\u0020]/.test(url);
function read(owner:string):Saved {
 if(stores.has(owner))return stores.get(owner)!;
 let saved:Saved={pins:[],collapsed:false};
 try { const item=JSON.parse(localStorage.getItem(key)||'{}')[owner]; if(item) saved={collapsed:item.collapsed===true,pins:Array.isArray(item.pins)?item.pins.filter((p:Pin)=>p && typeof p.id==='string' && typeof p.name==='string' && p.name.trim() && typeof p.url==='string' && validPinUrl(p.url) && typeof p.created==='string').slice(0,8):[]}; } catch { /* Optional storage. */ }
 stores.set(owner,saved);return saved;
}
function write(owner:string,saved:Saved) {
 stores.set(owner,saved);
 try { const all=JSON.parse(localStorage.getItem(key)||'{}'); localStorage.setItem(key,JSON.stringify({...all,[owner]:saved})); } catch { /* Keep this session usable. */ }
 emit();
}
export function usePins(owner:string) {
 const saved=useSyncExternalStore(subscribe,()=>read(owner),()=>read(owner));
 return {...saved,
  add(name:string,url:string) { const current=read(owner); if(!validPinUrl(url)||!name.trim())return 'Invalid view'; if(current.pins.some(p=>p.url===url))return 'This view is already pinned'; if(current.pins.length>=8)return 'Remove a pinned view before adding another'; write(owner,{...current,pins:[...current.pins,{id:crypto.randomUUID(),name:name.trim().slice(0,120),url,created:new Date().toISOString()}]});return 'View pinned'; },
  rename(id:string,name:string) { if(name.trim())write(owner,{...read(owner),pins:read(owner).pins.map(p=>p.id===id?{...p,name:name.trim().slice(0,120)}:p)}); },
  remove(id:string) { write(owner,{...read(owner),pins:read(owner).pins.filter(p=>p.id!==id)}); },
  move(id:string,delta:number) { const pins=[...read(owner).pins];const from=pins.findIndex(p=>p.id===id),to=from+delta;if(from<0||to<0||to>=pins.length)return;const [pin]=pins.splice(from,1);pins.splice(to,0,pin);write(owner,{...read(owner),pins}); },
  collapse(collapsed:boolean) { write(owner,{...read(owner),collapsed}); },
 };
}
export function usePinView(name:string,overrides:Record<string,string>={}) {
 const {me}=useSession();const pins=usePins(`${me.tenant_id}:${me.id}`);const location=useLocation();const toast=useToast();
 const params=new URLSearchParams(location.search);Object.entries(overrides).forEach(([key,value])=>{if(value)params.set(key,value);else params.delete(key);});
 const query=params.toString();const url=location.pathname+(query?'?'+query:'')+location.hash;
 return {label:'Pin this view',onSelect:()=>toast.show({message:pins.add(name,url)})};
}
